/**
 * @fileoverview AR-670 — scenario switch and in-memory session state of the
 * mock Classroom adapter. Split from mockAdapter.ts so each file stays focused
 * (and under the project's 199-line ceiling).
 *
 * QA/dev scenario (read on every call — no reload needed):
 *   localStorage.setItem('classroom-mock-scenario', '<scenario>')
 */

import {
  ProjectLinkResult,
  ProjectLinkState,
  RosterSummary,
  SchoolConnection,
} from '../types'
import { MOCK_ROSTER_STUDENTS, MOCK_ROSTER_SUMMARY, MOCK_ROSTER_SUMMARY_ALL_UNMATCHED } from './mockData'

export const CLASSROOM_MOCK_SCENARIO_KEY = 'classroom-mock-scenario'

type MockScenario =
  | 'connected'
  | 'disconnected'
  | 'needs_reconnection'
  | 'empty'
  | 'already_linked'
  | 'course_already_linked'
  | 'link_roster_error'
  | 'roster_all_unmatched'
  | 'missing_announcements_scope'
  | 'grade_student_not_linked'
  | 'grade_no_submission'
  | 'grade_not_assignment_owner'
  | 'grade_needs_reconnection'

export function scenario(): MockScenario {
  try {
    return (window.localStorage.getItem(CLASSROOM_MOCK_SCENARIO_KEY) as MockScenario) || 'connected'
  } catch {
    return 'connected'
  }
}

const MOCK_LATENCY_MS = 450

export function delay(): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, MOCK_LATENCY_MS))
}

export const CONNECTED_SCHOOL: SchoolConnection = {
  connected: true,
  status: 'connected',
  googleEmail: 'admin@arcmosaico.ai',
  workspaceDomain: 'arcmosaico.ai',
  connectedAt: '2026-05-02T10:00:00Z',
}

/* --------------------------- session state --------------------------- */

let connectionOverride: SchoolConnection | null = null

const projectLinks = new Map<string, ProjectLinkState>()

export function setConnectionOverride(next: SchoolConnection | null): void {
  connectionOverride = next
}

export function currentConnection(): SchoolConnection {
  if (connectionOverride) return connectionOverride
  switch (scenario()) {
    case 'disconnected':
      return { connected: false, status: 'disconnected' }
    case 'needs_reconnection':
      return { ...CONNECTED_SCHOOL, connected: false, status: 'needs_reconnection' }
    default:
      return CONNECTED_SCHOOL
  }
}

export function readProjectLink(projectId: string): ProjectLinkState | undefined {
  return projectLinks.get(projectId)
}

export function writeProjectLink(projectId: string, state: ProjectLinkState): void {
  projectLinks.set(projectId, state)
}

export function clearProjectLink(projectId: string): void {
  projectLinks.delete(projectId)
}

export function baseProjectLink(): ProjectLinkState {
  return {
    connectionStatus: currentConnection().status,
    linked: false,
    syncEnabled: false,
    courseId: null,
    courseName: null,
    courseLink: null,
    origin: null,
    mappedStudentCount: 0,
    lastRosterSyncAt: null,
  }
}

/**
 * State right after POST /link: the matching runs synchronously, so the card
 * is born with the matched roster (unless the matching failed — see the
 * `link_roster_error` scenario in the adapter).
 */
export function linkedProjectState(courseId: string, courseName: string): ProjectLinkState {
  const summary = rosterForScenario()
  return {
    connectionStatus: 'connected',
    linked: true,
    syncEnabled: true,
    courseId,
    courseName,
    courseLink: `https://classroom.google.com/c/mock-${courseId}`,
    origin: 'linked',
    mappedStudentCount: summary.mappedStudentsCount,
    lastRosterSyncAt: new Date().toISOString(),
    rosterStudents: summary.matchedCount > 0 ? MOCK_ROSTER_STUDENTS : [],
    unmatchedStudents: summary.unmatched,
  }
}

export function rosterForScenario(): RosterSummary {
  return scenario() === 'roster_all_unmatched' ? MOCK_ROSTER_SUMMARY_ALL_UNMATCHED : MOCK_ROSTER_SUMMARY
}

export function toLinkResult(
  state: ProjectLinkState,
  roster: RosterSummary | null,
  rosterError: string | null = null,
): ProjectLinkResult {
  return {
    linked: state.linked,
    syncEnabled: state.syncEnabled,
    courseId: state.courseId,
    courseName: state.courseName,
    courseLink: state.courseLink,
    roster,
    rosterError,
  }
}
